import type { ReactNode } from 'react'
import { Info, AlertTriangle } from 'lucide-react'

export type ListItem = string | { term: string; text: string }

export type ContentBlock =
    | { kind: 'lead'; text: string }
    | { kind: 'para'; text: string }
    | { kind: 'heading'; text: string }
    | { kind: 'subheading'; text: string }
    | { kind: 'list'; ordered?: boolean; items: ListItem[] }
    | { kind: 'note'; text: string }
    | { kind: 'callout'; tone?: 'info' | 'warn'; title?: string; text: string }
    | { kind: 'divider' }
    | { kind: 'table'; columns: string[]; rows: string[][] }
    | { kind: 'steps'; items: { title: string; text: string }[] }
    | { kind: 'keyvalue'; title?: string; items: [string, string][] }

export function slugifyHeading(text: string): string {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
}

export function headingsOf(blocks: ContentBlock[]): { id: string; text: string }[] {
    return blocks
        .filter((b): b is { kind: 'heading'; text: string } => b.kind === 'heading')
        .map((b) => ({ id: slugifyHeading(b.text), text: b.text }))
}

function ListBlock({ ordered, items }: { ordered?: boolean; items: ListItem[] }) {
    const lis = items.map((it, i) =>
        typeof it === 'string' ? (
            <li key={i}>{it}</li>
        ) : (
            <li key={i}>
                <strong>{it.term}:</strong> {it.text}
            </li>
        )
    )
    return ordered ? <ol>{lis}</ol> : <ul>{lis}</ul>
}

function Block({ block }: { block: ContentBlock }): ReactNode {
    switch (block.kind) {
        case 'lead':
            return (
                <p
                    className="not-prose mb-7"
                    style={{
                        fontSize: 'clamp(1.1rem, 1.8vw, 1.3rem)',
                        lineHeight: 1.55,
                        color: 'var(--color-ink-700)',
                        fontFamily: 'var(--font-display)'
                    }}
                >
                    {block.text}
                </p>
            )
        case 'heading':
            return (
                <h2 id={slugifyHeading(block.text)} style={{ scrollMarginTop: '6rem' }}>
                    {block.text}
                </h2>
            )
        case 'subheading':
            return <h3>{block.text}</h3>
        case 'para':
            return <p>{block.text}</p>
        case 'list':
            return <ListBlock ordered={block.ordered} items={block.items} />
        case 'divider':
            return <hr className="rule-double not-prose my-10" />
        case 'note':
            return (
                <div
                    className="not-prose my-6 px-5 py-4"
                    style={{
                        background: 'var(--color-paper-100)',
                        borderLeft: '3px solid var(--color-gold-500)',
                        borderRadius: '2px',
                        color: 'var(--color-ink-600)',
                        fontSize: '0.95rem',
                        lineHeight: 1.7
                    }}
                >
                    {block.text}
                </div>
            )
        case 'callout': {
            const warn = block.tone === 'warn'
            const Icon = warn ? AlertTriangle : Info
            return (
                <div
                    className="not-prose my-6 px-5 py-4 flex gap-3.5"
                    style={{
                        background: 'var(--color-paper-100)',
                        borderLeft: `3px solid ${warn ? 'var(--color-accent-500)' : 'var(--color-gold-500)'}`,
                        borderRadius: '2px'
                    }}
                >
                    <Icon size={18} style={{ color: warn ? 'var(--color-accent-600)' : 'var(--color-gold-700)', flexShrink: 0, marginTop: 3 }} />
                    <div>
                        {block.title && (
                            <div className="font-display" style={{ fontSize: '1.02rem', color: 'var(--color-ink-700)' }}>
                                {block.title}
                            </div>
                        )}
                        <div
                            className={block.title ? 'mt-1' : ''}
                            style={{ color: 'var(--color-ink-600)', fontSize: '0.95rem', lineHeight: 1.7 }}
                        >
                            {block.text}
                        </div>
                    </div>
                </div>
            )
        }
        case 'steps':
            return (
                <ol className="not-prose my-6 list-none p-0" style={{ borderTop: '1px solid var(--color-rule)' }}>
                    {block.items.map((s, i) => (
                        <li
                            key={i}
                            className="grid grid-cols-[2.25rem_1fr] gap-x-4 py-4"
                            style={{ borderBottom: '1px solid var(--color-rule)' }}
                        >
                            <span
                                className="inline-flex items-center justify-center"
                                style={{ width: '1.9rem', height: '1.9rem', background: 'var(--color-gold-700)', color: 'white', borderRadius: '50%', fontFamily: 'var(--font-display)', fontSize: '0.85rem' }}
                            >
                                {i + 1}
                            </span>
                            <div>
                                <span className="font-display" style={{ fontSize: '1.02rem', color: 'var(--color-ink-700)' }}>
                                    {s.title}.
                                </span>{' '}
                                <span style={{ fontSize: '0.96rem', color: 'var(--color-ink-600)', lineHeight: 1.7 }}>
                                    {s.text}
                                </span>
                            </div>
                        </li>
                    ))}
                </ol>
            )
        case 'keyvalue':
            return (
                <div className="not-prose my-6" style={{ border: '1px solid var(--color-rule-strong)', background: 'var(--color-paper-50)' }}>
                    {block.title && (
                        <div
                            className="px-5 py-3 text-[11px] uppercase font-semibold"
                            style={{ letterSpacing: '0.16em', color: 'var(--color-paper-50)', background: 'var(--color-gold-800)' }}
                        >
                            {block.title}
                        </div>
                    )}
                    <dl className="px-5 py-1">
                        {block.items.map(([k, v], i) => (
                            <div
                                key={i}
                                className="grid sm:grid-cols-[14rem_1fr] gap-x-4 py-3"
                                style={{ borderBottom: i < block.items.length - 1 ? '1px solid var(--color-rule)' : 'none' }}
                            >
                                <dt className="text-[13px] font-semibold" style={{ color: 'var(--color-ink-500)' }}>
                                    {k}
                                </dt>
                                <dd className="text-[14px]" style={{ color: 'var(--color-ink-700)' }}>
                                    {v}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            )
        case 'table':
            return (
                <div className="not-prose my-7 overflow-x-auto" style={{ border: '1px solid var(--color-rule-strong)' }}>
                    <table className="w-full border-collapse" style={{ fontSize: '13.5px' }}>
                        <thead>
                            <tr style={{ background: 'var(--color-gold-800)' }}>
                                {block.columns.map((c, i) => (
                                    <th
                                        key={i}
                                        className="text-left px-4 py-3 font-semibold uppercase"
                                        style={{ color: 'var(--color-paper-50)', letterSpacing: '0.08em', fontSize: '11px', borderRight: i < block.columns.length - 1 ? '1px solid rgba(243,236,217,0.15)' : 'none' }}
                                    >
                                        {c}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {block.rows.map((row, ri) => (
                                <tr key={ri} style={{ background: ri % 2 ? 'var(--color-paper-100)' : 'var(--color-paper-50)' }}>
                                    {row.map((cell, ci) => (
                                        <td
                                            key={ci}
                                            className="px-4 py-3 align-top"
                                            style={{
                                                color: 'var(--color-ink-600)',
                                                borderTop: '1px solid var(--color-rule)',
                                                borderRight: ci < row.length - 1 ? '1px solid var(--color-rule)' : 'none',
                                                fontWeight: ci === 0 ? 600 : 400
                                            }}
                                        >
                                            {cell}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )
    }
}

export default function StructuredContent({ blocks }: { blocks: ContentBlock[] }) {
    return (
        <div className="wp-prose">
            {blocks.map((b, i) => (
                <Block key={i} block={b} />
            ))}
        </div>
    )
}
